'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Check, Copy, InfoIcon } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { config } from '@/lib/config'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Schema
// ============================================================================

const credentialsSchema = z.object({
  publicUsername: z.string().min(1, 'Public username is required'),
  publicPassword: z.string().min(1, 'Public password is required'),
  privateUsername: z.string().min(1, 'Private username is required'),
  privatePassword: z.string().min(1, 'Private password is required'),
  secretPassphrase: z.string().optional(),
  applePayMerchantId: z.string().optional(),
})

type CredentialsValues = z.infer<typeof credentialsSchema>

interface HiPayFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
  storeId?: string
}

// ============================================================================
// Component
// ============================================================================

export function HiPayForm({ defaultValues = {}, onSubmit, isLoading = false }: HiPayFormProps) {
  const { t } = useTranslation('integrations')
  const { toast } = useToast()
  const hasExistingCredentials = !!(defaultValues.credentials?.publicUsername)

  const [sandboxed, setSandboxed] = useState(defaultValues.credentials?.sandboxed ?? false)
  const [copiedWebhook, setCopiedWebhook] = useState(false)

  const form = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      publicUsername: defaultValues.credentials?.publicUsername ?? '',
      publicPassword: defaultValues.credentials?.publicPassword ?? '',
      privateUsername: defaultValues.credentials?.privateUsername ?? '',
      privatePassword: defaultValues.credentials?.privatePassword ?? '',
      secretPassphrase: defaultValues.credentials?.secretPassphrase ?? '',
      applePayMerchantId: defaultValues.credentials?.applePayMerchantId ?? '',
    },
  })

  const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/hipay`

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopiedWebhook(true)
      setTimeout(() => setCopiedWebhook(false), 2000)
      toast({ title: t('hipay.copied'), description: t('hipay.copiedDescription') })
    } catch {
      toast({ title: t('hipay.copyError'), description: t('hipay.copyErrorDescription'), variant: 'destructive' })
    }
  }

  const handleFormSubmit = async (data: CredentialsValues) => {
    await onSubmit({
      credentials: {
        ...data,
        sandboxed,
        secretPassphrase: data.secretPassphrase || undefined,
        applePayMerchantId: data.applePayMerchantId || undefined,
      },
    })
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      {hasExistingCredentials && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">{t('hipay.integrationConnected')}</AlertTitle>
          <AlertDescription className="text-green-700">
            {t('hipay.connectedDescription')}
          </AlertDescription>
        </Alert>
      )}

      <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* API Credentials */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hipay.apiCredentials')}</CardTitle>
            <CardDescription dangerouslySetInnerHTML={{ __html: t('hipay.apiCredentialsDescription') }} />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Public Credentials */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {t('hipay.publicCredentials')}
              </h4>

              <div className="space-y-2">
                <Label htmlFor="publicUsername">
                  {t('hipay.publicUsername')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="publicUsername"
                  placeholder={t('hipay.publicUsernamePlaceholder')}
                  disabled={isLoading}
                  {...form.register('publicUsername')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('hipay.publicUsernameHint')}
                </p>
                {form.formState.errors.publicUsername && (
                  <p className="text-xs text-destructive">{form.formState.errors.publicUsername.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="publicPassword">
                  {t('hipay.publicPassword')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="publicPassword"
                  type="password"
                  placeholder={t('hipay.publicPasswordPlaceholder')}
                  disabled={isLoading}
                  {...form.register('publicPassword')}
                />
                {form.formState.errors.publicPassword && (
                  <p className="text-xs text-destructive">{form.formState.errors.publicPassword.message}</p>
                )}
              </div>
            </div>

            <hr />

            {/* Private Credentials */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {t('hipay.privateCredentials')}
              </h4>

              <div className="space-y-2">
                <Label htmlFor="privateUsername">
                  {t('hipay.privateUsername')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="privateUsername"
                  placeholder={t('hipay.privateUsernamePlaceholder')}
                  disabled={isLoading}
                  {...form.register('privateUsername')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('hipay.privateUsernameHint')}
                </p>
                {form.formState.errors.privateUsername && (
                  <p className="text-xs text-destructive">{form.formState.errors.privateUsername.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="privatePassword">
                  {t('hipay.privatePassword')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="privatePassword"
                  type="password"
                  placeholder={t('hipay.privatePasswordPlaceholder')}
                  disabled={isLoading}
                  {...form.register('privatePassword')}
                />
                {form.formState.errors.privatePassword && (
                  <p className="text-xs text-destructive">{form.formState.errors.privatePassword.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Webhook */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hipay.webhookTitle')}</CardTitle>
            <CardDescription>
              <span dangerouslySetInnerHTML={{ __html: t('hipay.webhookDescription') }} />{' '}
              <a
                href="https://developer.hipay.com/payment-fundamentals/requirements/notifications"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                {t('hipay.webhookDocs')}
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('hipay.notificationUrl')}</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-xs break-all">{webhookUrl}</code>
                <Button type="button" variant="outline" size="sm" onClick={handleCopyWebhook}>
                  {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secretPassphrase">{t('hipay.secretPassphrase')}</Label>
              <Input
                id="secretPassphrase"
                type="password"
                placeholder={t('hipay.secretPassphrasePlaceholder')}
                disabled={isLoading}
                {...form.register('secretPassphrase')}
              />
              <p className="text-xs text-muted-foreground">
                {t('hipay.secretPassphraseHint')}
              </p>
            </div>

            <div className="rounded-lg border border-muted bg-muted/30 p-4 space-y-3">
              <h4 className="text-sm font-medium">{t('hipay.notificationSettingsTitle')}</h4>
              <ul className="text-xs text-muted-foreground space-y-2 list-disc list-inside">
                <li dangerouslySetInnerHTML={{ __html: t('hipay.requestMethod') }} />
                <li>
                  <span dangerouslySetInnerHTML={{ __html: t('hipay.transactionStatuses') }} />
                  <ul className="mt-1 ml-4 list-[circle]">
                    <li dangerouslySetInnerHTML={{ __html: t('hipay.successStatuses') }} />
                    <li dangerouslySetInnerHTML={{ __html: t('hipay.declineStatuses') }} />
                  </ul>
                </li>
                <li dangerouslySetInnerHTML={{ __html: t('hipay.fields') }} />
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Apple Pay */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hipay.applePayTitle')}</CardTitle>
            <CardDescription>
              {t('hipay.applePayDescription')}{' '}
              <code className="text-xs">merchant.com.hipay.yourmerchantname</code>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="applePayMerchantId">{t('hipay.applePayMerchantId')}</Label>
              <Input
                id="applePayMerchantId"
                placeholder="merchant.com.hipay.yourmerchantname"
                disabled={isLoading}
                {...form.register('applePayMerchantId')}
              />
              <p className="text-xs text-muted-foreground">
                {t('hipay.applePayMerchantIdHint')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hipay.settingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="sandboxed" className="font-medium">{t('hipay.sandboxMode')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('hipay.sandboxModeHint')} (
                  <code className="text-xs">stage-secure-gateway.hipay-tpp.com</code>)
                </p>
              </div>
              <Switch
                id="sandboxed"
                checked={sandboxed}
                onCheckedChange={setSandboxed}
                disabled={isLoading}
              />
            </div>
          </CardContent>
        </Card>

        {/* Setup Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>{t('hipay.setupInstructions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4 border-blue-200 bg-blue-50">
              <InfoIcon className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700 text-sm">
                {t('hipay.setupInfoAlert')}
              </AlertDescription>
            </Alert>
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('hipay.setupStep1')}{' '}
                <a
                  href="https://merchant.hipay-tpp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('hipay.setupStep1Link')}
                </a>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('hipay.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('hipay.setupStep3') }} />
              <li dangerouslySetInnerHTML={{ __html: t('hipay.setupStep4') }} />
              <li dangerouslySetInnerHTML={{ __html: t('hipay.setupStep5') }} />
            </ol>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
